"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useLanguage } from "../../app/components/context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { ChevronRight } from "lucide-react";
import { STRAPI_URL } from "@/lib/strapi";

type Event = {
  id: number;
  Title: string;
  slug: string;
  typeEvent: string;
  date: string;
  photocover: { url: string | null; alternativeText: string };
  description?: { type: string; children: { text: string }[] }[];
};

const ITEMS_PER_PAGE = 4;

export default function EventSection() {
  const { language } = useLanguage();
  const { theme } = useTheme();

  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

   
  const isDark = theme === "dark";

  /* ================= FETCH EVENTS ================= */
  const fetchEvents = async (lang: string) => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(
        `${STRAPI_URL}/api/eventsses?populate=*&locale=${lang}`,
        { cache: "no-store" }
      );

      const data = await res.json();

      const mappedEvents: Event[] = data.data.map((item: any) => ({
        id: item.id,
        Title: item.Title,
        slug: item.slug,
        typeEvent: item.typeEvent,
        date: item.date,
        description: item.description || [],
        photocover: {
          url: item.photocover?.formats?.large?.url
            ? `${STRAPI_URL}${item.photocover.formats.large.url}`
            : item.photocover?.url
            ? `${STRAPI_URL}${item.photocover.url}`
            : null,
          alternativeText: item.photocover?.alternativeText || "Event image",
        },
      }));

      const sorted = mappedEvents.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );

      setEvents(sorted);
    } catch (err) {
      console.error(err);
      setError("Failed to load events.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents(language);
  }, [language]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, language]);

  /* ================= FILTER ================= */
  const filteredEvents = events.filter((event) => {
    if (activeFilter === "all") return true;
    if (activeFilter === "upcoming") return new Date(event.date) >= new Date();
    if (activeFilter === "past") return new Date(event.date) < new Date();
    return event.typeEvent === activeFilter;
  });

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(filteredEvents.length / ITEMS_PER_PAGE);

  const paginatedEvents = filteredEvents.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  /* ================= LOADING ================= */
  if (loading)
    return (
      <section className={`py-20 ${isDark ? "bg-gray-900" : "bg-white"}`}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div
                  className={`aspect-[16/9] rounded-2xl ${
                    isDark ? "bg-gray-800" : "bg-gray-200"
                  }`}
                />
                <div
                  className={`h-14 mt-3 rounded-xl ${
                    isDark ? "bg-gray-800" : "bg-gray-100"
                  }`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    );

  /* ================= ERROR ================= */
  if (error)
    return (
      <section className={`py-20 ${isDark ? "bg-gray-900" : "bg-white"}`}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className={`mb-6 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            {error}
          </p>
          <button
            onClick={() => fetchEvents(language)}
            className="px-6 py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
          >
            {language === "fr" && "Réessayer"}
            {language === "en" && "Try Again"}
            {language === "ar" && "إعادة المحاولة"}
          </button>
        </div>
      </section>
    );

  /* ================= UI ================= */
  return (
    <section className={`py-20 ${isDark ? "bg-gray-900" : "bg-white"}`}>
      <div className="max-w-6xl mx-auto px-4">
        {/* ============ HEADER (minimal) ============ */}
        <div className="mb-10">
          <h2
            className={`text-3xl md:text-4xl font-semibold mb-3 ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            {language === "fr" && "Nos Événements"}
            {language === "en" && "Our Events"}
            {language === "ar" && "فعالياتنا"}
          </h2>

          <p
            className={`text-base max-w-2xl ${
              isDark ? "text-gray-400" : "text-gray-600"
            }`}
          >
            {language === "fr" &&
              "Découvrez nos prochains événements et rejoignez-nous."}
            {language === "en" &&
              "Discover our upcoming events and join us."}
            {language === "ar" &&
              "اكتشف فعالياتنا القادمة وانضم إلينا."}
          </p>
        </div>

        {/* ============ FILTERS (text-only, underline) ============ */}
        <div
          className={`flex flex-wrap items-center gap-8 pb-4 mb-8 border-b ${
            isDark ? "border-gray-800" : "border-gray-200"
          }`}
        >
          {[
            { key: "all", label: { fr: "Tous", en: "All", ar: "الكل" } },
            {
              key: "upcoming",
              label: { fr: "À venir", en: "Upcoming", ar: "القادمة" },
            },
            {
              key: "past",
              label: { fr: "Passés", en: "Past", ar: "الماضية" },
            },
          ].map(({ key, label }) => {
            const active = activeFilter === key;
            return (
              <button
                key={key}
                onClick={() => setActiveFilter(key)}
                className={`relative pb-2 text-[14px] font-medium transition-colors ${
                  active
                    ? isDark
                      ? "text-white"
                      : "text-gray-900"
                    : isDark
                    ? "text-gray-500 hover:text-gray-300"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {label[language as keyof typeof label]}
                {active && (
                  <span
                    className={`absolute left-0 right-0 -bottom-[17px] h-[3px] rounded-full ${
                      isDark ? "bg-white" : "bg-gray-900"
                    }`}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* ============ EVENTS GRID (2 columns) ============ */}
        {paginatedEvents.length === 0 ? (
          <p
            className={`text-center py-20 ${
              isDark ? "text-gray-500" : "text-gray-500"
            }`}
          >
            {language === "fr" && "Aucun événement"}
            {language === "en" && "No events"}
            {language === "ar" && "لا توجد فعاليات"}
          </p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {paginatedEvents.map((event) => (
           

 <Link
  key={event.id}
  href={`/Events/${event.slug}`}
  className={`
    group block overflow-hidden shadow-sm hover:shadow-md
    transition-all duration-300
    ${isDark ? "bg-gray-800" : "bg-white"}
  `}
>
  {/* ---- Image ---- */}
  <div
    className={`relative w-full aspect-[16/9] overflow-hidden ${
      isDark ? "bg-gray-950" : "bg-gray-100"
    }`}
  >
    {event.photocover.url ? (
      <img
        src={event.photocover.url}
        alt={event.photocover.alternativeText}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
    ) : (
      <div className="w-full h-full flex items-center justify-center text-gray-400">
        No image
      </div>
    )}
  </div>

  {/* ---- Footer bar ---- */}
  <div
    className={`
      flex items-center justify-between
      px-5 py-4
      ${isDark ? "bg-gray-800" : "bg-white"}
    `}
  >
    <span
      className={`text-[17px] font-medium ${
        isDark ? "text-white" : "text-gray-900"
      }`}
    >
      {event.Title}
    </span>

    <ChevronRight
      className={`w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 ${
        isDark ? "text-white" : "text-gray-900"
      }`}
      strokeWidth={2}
    />
  </div>
</Link>
            ))}
          </div>
        )}

        {/* ============ PAGINATION ============ */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-12">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className={`
                px-5 py-2.5 rounded-lg text-[14px] font-medium transition-colors
                ${currentPage === 1 ? "opacity-40 cursor-not-allowed" : ""}
                ${
                  isDark
                    ? "bg-gray-800 text-gray-200 hover:bg-gray-700"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }
              `}
            >
              {language === "fr" && "Précédent"}
              {language === "en" && "Previous"}
              {language === "ar" && "السابق"}
            </button>

            <span
              className={`text-[13px] ${
                isDark ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {currentPage} / {totalPages}
            </span>

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className={`
                px-5 py-2.5 rounded-lg text-[14px] font-medium transition-colors
                ${
                  currentPage === totalPages
                    ? "opacity-40 cursor-not-allowed"
                    : ""
                }
                ${
                  isDark
                    ? "bg-white text-gray-900 hover:bg-gray-100"
                    : "bg-gray-900 text-white hover:bg-gray-800"
                }
              `}
            >
              {language === "fr" && "Suivant"}
              {language === "en" && "Next"}
              {language === "ar" && "التالي"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}